<template>
  <v-row dense>
    <v-col lg="12" cols="12" class="pt-0">
      <v-row dense>
        <v-col class="pa-0 ma-0" lg="12" cols="12">
          <PriceInfo ref="PriceInfo" @onRowUpdate="handleRowUpdate" />
          <MainDetails ref="MainDetails" />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col lg="12" cols="12" class="pt-0">
          <v-data-table
            calculate-widths
            show-select
            fixed-header
            class="elevation-1"
            loading-text="Loading... Please wait"
            item-key="id"
            v-model="selected"
            :headers="getFilteredHeaders()"
            :items="internalRows"
            :item-class="getRowItemClass"
            :groupBy="groupBy"
            :loading="loading || internalLoading"
            :server-items-length="totalItems"
            :options.sync="pagination"
            :footer-props="footerProps"
          >
            <template v-slot:item.info_section="{ item }">
              <div class="item-warning" v-if="!isItemValid(item)">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon color="warning" v-on="on">
                      mdi-alert-decagram
                    </v-icon>
                  </template>
                  <div>
                    <p class="mb-0"><b>The item is invalid:</b></p>
                    <ul class="pl-4">
                      <li v-if="!item.rhapsody_name">Rhapsody name missed</li>
                      <li v-if="!item.subcategory">Subcategory missed</li>
                    </ul>
                  </div>
                </v-tooltip>
              </div>
            </template>

            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length">
                <h5>
                  There would be a table. The same as the main table but with
                  historical information {{ item.id }}
                </h5>
              </td>
            </template>

            <!-- Status -->
            <template v-slot:item.status="{ item }">
              <v-tooltip bottom max-width="200">
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    :color="getStatusByName(item.status.name).color"
                    @click="(e) => showStatusChangeMenu(e, { item })"
                    v-on="on"
                  >
                    <v-icon>
                      {{ getStatusByName(item.status.name).icon }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>
                  {{ item.status.title }}.
                  <br />
                </span>
                <span>Click to change a status</span>
              </v-tooltip>
            </template>

            <!-- lastComment -->
            <template v-slot:item.lastComment="{ item }">
              <template v-if="item.lastComment && item.lastComment.id">
                <div style="cursor: pointer" @click="() => showComments(item)">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon
                        class="mr-2"
                        v-on="on"
                        color="primary"
                        size="32"
                        v-if="
                          item.lastComment.user &&
                          item.lastComment.user.id === 1
                        "
                      >
                        mdi-robot
                      </v-icon>
                      <v-icon
                        class="mr-2"
                        v-on="on"
                        color="yellow"
                        size="32"
                        v-else-if="
                          item.lastComment.user &&
                          item.lastComment.user.id === 2
                        "
                      >
                        mdi-face
                      </v-icon>

                      <v-avatar
                        class="mr-2"
                        size="32"
                        v-on="on"
                        v-else-if="
                          item.lastComment.user && item.lastComment.user.picture
                        "
                      >
                        <img
                          :src="
                            item.lastComment.user &&
                            item.lastComment.user.picture
                              ? item.lastComment.user.picture
                              : ''
                          "
                        />
                      </v-avatar>
                    </template>
                    <span
                      v-if="
                        item.lastComment.user && item.lastComment.user.id === 1
                      "
                    >
                      System
                    </span>
                    <span
                      v-else-if="
                        item.lastComment.user && item.lastComment.user.id === 2
                      "
                    >
                      Customer with protected link
                    </span>
                    <span v-else>
                      {{
                        item.lastComment.user ? item.lastComment.user.email : ''
                      }}
                    </span>
                  </v-tooltip>
                  <span>{{ item.lastComment.message }}</span>
                </div>
              </template>
              <template class="text-center" v-else>
                <span>No comments yet...</span>
              </template>
            </template>

            <!-- Multimatch -->
            <template v-slot:item.multimatch_selection="{ item }">
              <v-checkbox
                :disabled="item.status.name === 'imported'"
                class="multimatch-radio-group"
                v-model="multiMatchModels[item.multimatch]"
                @change="() => multiMatchSelected(item)"
                :value="item.id"
              />
            </template>

            <!-- Variation -->
            <template v-slot:item.variation="{ item }">
              <FileTableEditableItem
                key-value="variation"
                :value="$v(item, 'variation')"
                :is-row-disabled="isRowDisabled(item)"
                @onItemUpdate="
                  (updatePartition) => handleRowUpdate(item.id, updatePartition)
                "
              />
            </template>

            <!-- Rhapsody Code -->
            <template v-slot:item.rhapsody_code="{ item }">
              <FileTableEditableItem
                key-value="rhapsody_code_override"
                :value="
                  $v(item, 'rhapsody_code_override') ||
                  $v(item, 'rhapsody_code')
                "
                :is-row-disabled="isRowDisabled(item)"
                @onItemUpdate="
                  (updatePartition) => handleRowUpdate(item.id, updatePartition)
                "
              />
            </template>

            <!-- obsolete -->
            <template v-slot:item.propsStructure.obsolete="{ item }">
              <ObsoleteStatus :row="item" />
            </template>

            <!-- compounded -->
            <template v-slot:item.propsStructure.compounded="{ item }">
              <CompoundedStatus
                :row="item"
                @onCompoundedChanged="
                  (value) =>
                    $emit(
                      'onCompoundedChanged',
                      item.id,
                      item.propsStructure.id,
                      value
                    )
                "
              />
            </template>

            <!-- Analyze status -->
            <template v-slot:item.analyze_status="{ item }">
              <AnalyzeStatus :row="item" />
            </template>

            <!-- Import status -->
            <template v-slot:item.import_status="{ item }">
              <ImportStatus :row="item" />
            </template>

            <!-- Price -->
            <template v-slot:item.priceInfo.price="{ item }">
              <FileTableEditableItem
                price-field
                key-value="priceInfo.price"
                :value="$v(item, 'priceInfo.price')"
                :is-row-disabled="isRowDisabled(item)"
                @onItemUpdate="
                  (updatePartition) => handleRowUpdate(item.id, updatePartition)
                "
              >
                <template #actions>
                  <v-tooltip bottom color="white">
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        @click="
                          () => $refs.PriceInfo.open(item, isRowDisabled(item))
                        "
                        icon
                      >
                        <v-icon>mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                    <span class="black--text">
                      Click to see more Price information
                    </span>
                  </v-tooltip>
                  <template
                    v-if="
                      item.priceStructure.extendProperties &&
                      item.priceStructure.extendProperties.costComment
                    "
                  >
                    <v-tooltip bottom color="white">
                      <template v-slot:activator="{ on }">
                        <v-icon color="warning" v-on="on">
                          mdi-alert-outline
                        </v-icon>
                      </template>
                      <span class="black--text">
                        {{
                          item.priceStructure.extendProperties &&
                          item.priceStructure.extendProperties.costComment
                        }}
                      </span>
                    </v-tooltip>
                  </template>
                </template>
              </FileTableEditableItem>
            </template>

            <!-- minimum Price -->
            <template v-slot:item.priceInfo.minimum_price="{ item }">
              <FileTableEditableItem
                price-field
                key-value="priceInfo.minimum_price"
                :value="$v(item, 'priceInfo.minimum_price')"
                :is-row-disabled="isRowDisabled(item)"
                @onItemUpdate="
                  (updatePartition) => handleRowUpdate(item.id, updatePartition)
                "
              />
            </template>

            <!-- GL code -->
            <template v-slot:item.custom_gl_code="{ item }">
              <FileTableEditableItem
                key-value="custom_gl_code"
                :value="item.custom_gl_code"
                :is-row-disabled="isRowDisabled(item)"
                @onItemUpdate="
                  (updatePartition) => handleRowUpdate(item.id, updatePartition)
                "
              />
            </template>

            <!-- Score / match_weight -->
            <template v-slot:item.match_weight="{ item }">
              <v-icon
                class="mr-auto"
                :color="getScoreIcon(item.match_weight).color"
              >
                {{ getScoreIcon(item.match_weight).icon }}
              </v-icon>
            </template>

            <!-- orig_external_code -->
            <template v-slot:item.orig_external_code="{ item }">
              <span>{{ item.matcher_feedback }}</span>
            </template>

            <!-- Actions -->
            <template v-slot:item.actions="{ item }">
              <div justify="center" align="center">
                <v-icon
                  class="mr-auto"
                  @click="
                    () => $refs.MainDetails.open(item, isRowDisabled(item))
                  "
                >
                  mdi-information-outline
                </v-icon>
              </div>
            </template>

            <!-- customer_verified -->
            <template v-slot:item.migrationPoll.checked="{ item }">
              <v-tooltip
                bottom
                :disabled="!$v(item, 'migrationPoll.checked')"
                color="white"
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    size="32"
                    :color="
                      $v(item, 'migrationPoll.checked') ? 'success' : '#d2d0d0'
                    "
                    v-on="on"
                  >
                    mdi-account-check
                  </v-icon>
                </template>
                <span class="black--text">
                  Item verified by the customer in Mig Pal
                </span>
              </v-tooltip>
            </template>

            <!-- reviewed -->
            <template v-slot:item.reviewed="{ item }">
              <v-tooltip bottom color="white">
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    :color="item.reviewed ? 'success' : '#d2d0d0'"
                    v-on="on"
                    @click.prevent="
                      () =>
                        !isRowDisabled(item) && handleRowReviewedChange(item)
                    "
                  >
                    <v-icon size="28">mdi-text-box-check</v-icon>
                  </v-btn>
                </template>
                <span class="black--text">
                  {{
                    item.reviewed
                      ? 'The row was marked as "Reviewed"'
                      : 'Mark as "Reviewed"'
                  }}
                </span>
              </v-tooltip>
            </template>

            <!-- Mapped core type override -->
            <template v-slot:item.mapped_core_type="{ item }">
              <FileTableEditableItem
                select-field
                keep-editable
                key-value="mapped_core_type_override"
                :edit-label="
                  item.mapped_core_type_override
                    ? 'Category override'
                    : 'Category type'
                "
                :list-options="getMappedTypeOverrideOptions(item)"
                :value="item.mapped_core_type_override || item.mapped_core_type"
                :is-row-disabled="isRowDisabled(item)"
                @onItemUpdate="
                  (updatePartition) => handleRowUpdate(item.id, updatePartition)
                "
              />
              <span
                class="caption text--secondary"
                v-if="
                  item.mapped_core_type_override ||
                  checkIsCategoryOverridable(item.category)
                "
              >
                mapped type: {{ item.mapped_core_type }}
              </span>
            </template>

            <!-- upc -->
            <template v-slot:item.upc="{ item }">
              <FileTableEditableItem
                key-value="upc"
                :value="$v(item, 'upc')"
                :is-row-disabled="isRowDisabled(item)"
                @onItemUpdate="
                  (updatePartition) => handleRowUpdate(item.id, updatePartition)
                "
              />
            </template>

            <!-- ndc -->
            <template v-slot:item.ndc="{ item }">
              <FileTableEditableItem
                key-value="ndc"
                :value="$v(item, 'ndc')"
                :is-row-disabled="isRowDisabled(item)"
                @onItemUpdate="
                  (updatePartition) => handleRowUpdate(item.id, updatePartition)
                "
              />
            </template>

            <!-- barcode -->
            <template v-slot:item.barcode="{ item }">
              <FileTableEditableItem
                key-value="barcode"
                :value="$v(item, 'barcode')"
                :is-row-disabled="isRowDisabled(item)"
                @onItemUpdate="
                  (updatePartition) => handleRowUpdate(item.id, updatePartition)
                "
              />
            </template>

            <!-- revenue percent-->
            <template v-slot:item.revenue_percent="{ item }">
              {{ item.revenue_percent || 0 }} %
            </template>

            <!-- dispensing fee -->
            <template v-slot:item.dispensing_fee="{ item }">
              <FileTableEditableItem
                is-nullable
                key-value="dispensing_fee"
                :value="$v(item, 'dispensing_fee')"
                :is-row-disabled="isRowDisabled(item)"
                @onItemUpdate="
                  (updatePartition) => handleRowUpdate(item.id, updatePartition)
                "
              />
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import _ from 'lodash';
import PriceInfo from './fileDetails/PriceInfo';
import MainDetails from './fileDetails/MainDetails';
import ImportStatus from './fileDetails/ImportStatus';
import AnalyzeStatus from './fileDetails/AnalyzeStatus';
import CompoundedStatus from './fileDetails/CompounedeStatus';
import ObsoleteStatus from './fileDetails/ObsoleteStatus';
import FileTableEditableItem from '@/components/common/FileTableEditableItem';
import { DEFAULTS, CategoriesList } from '@/constants';

const UNMATCHED_CATEGORY_NAME = 'unmatched';
const NonOverridableCategories = [
  'drug',
  'lab',
  'vaccine',
  'procedure',
  'diagnosis',
  'food',
  UNMATCHED_CATEGORY_NAME
];
const CategoriesObjList = CategoriesList.map((category) => ({
  text: category,
  value: category
}));
const CategoriesOptions = [
  ...CategoriesObjList,
  {
    text: UNMATCHED_CATEGORY_NAME,
    value: UNMATCHED_CATEGORY_NAME,
    disabled: true
  }
];
const CategoriesOverridableOptions = CategoriesOptions.filter(
  ({ value }) => !NonOverridableCategories.includes(value)
);

export default {
  name: 'FileTable',
  computed: {},
  props: {
    headerName: {
      type: String,
      default: ''
    },
    role: {
      type: String,
      default: ''
    },
    rows: {
      type: Array,
      default: () => []
    },
    headers: {
      type: Array,
      default: () => []
    },
    analysingRows: {
      type: Array,
      default: () => []
    },
    importingRows: {
      type: Array,
      default: () => []
    },
    params: {
      type: Object,
      default: () => ({})
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    menuRowId: {
      type: Number,
      default: undefined
    },
    totalItems: {
      type: Number,
      default: 0
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  components: {
    PriceInfo,
    MainDetails,
    ImportStatus,
    AnalyzeStatus,
    CompoundedStatus,
    ObsoleteStatus,
    FileTableEditableItem
  },
  data: () => ({
    x: 0,
    y: 0,
    editableItem: undefined,
    editableType: undefined,
    drugsTub: 'Drugs',
    selectedRow: undefined,
    selected: [],
    footerProps: {
      'items-per-page-options': [20, 40, 80, 150, 300],
      'show-current-page': true,
      'show-first-last-page': true
    },
    pagination: {
      page: 1,
      itemsPerPage: 20
    },
    multiMatchModels: {},
    groupBy: undefined,
    internalRows: [],
    internalLoading: true,
    latestCall: undefined,
    editTimeouts: {},
    editableValues: {}
  }),
  watch: {
    pagination: {
      deep: true,
      handler(newVal, oldVal) {
        if (!_.isEqual(newVal, oldVal)) {
          this.$emit('onPaginationChange', this.pagination);
        }
      }
    },
    rows(newValue) {
      this.reCreateMultiMatch();
      this.getGroupByField();
      this.internalRows = [];
      this.internalLoading = true;
      const date = new Date();
      this.latestCall = date;
      this.recurentTableSet(newValue, 1, date, true);
    },
    selected(newVal, oldVal) {
      if (!_.isEqual(newVal, oldVal)) {
        this.$emit('onRowsSelected', newVal);
      }
    }
  },
  methods: {
    isItemValid(item) {
      const category = _.get(item, 'category');
      const rhapsodyName = _.get(item, 'rhapsody_name');
      const subcategory = _.get(item, 'subcategory');
      const isItemIgnored = category === 'ignore';

      return isItemIgnored ? true : Boolean(rhapsodyName && subcategory);
    },
    getRowItemClass(item) {
      const isGoodMatch = _.eq(_.get(item, 'match_weight', 0), 1);
      const isReadyForImport = _.eq(
        _.get(item, ['status', 'name']),
        'ready_for_import'
      );
      const isItemValid = this.isItemValid(item);
      const isItemRecommendedForImport = isGoodMatch && !isReadyForImport;

      if (!isItemValid) {
        return 'item-invalid';
      }

      if (isItemRecommendedForImport) {
        return 'recommended-for-import';
      }

      return '';
    },
    getScoreIcon(score) {
      //  if score = 1 = smiley face, or green
      // 12:46
      // if score > .95 && < 1,  ok face, or blue
      // 12:47
      // if score > 0 and < .95, yellow

      switch (true) {
        case parseInt(score) === 1:
          return {
            icon: 'mdi-emoticon-excited-outline',
            color: '#7DAC1E'
          };

        case score > 0.95 && score < 1:
          return {
            icon: 'mdi-emoticon-neutral-outline',
            color: 'primary'
          };

        default:
          return {
            icon: 'mdi-emoticon-confused-outline',
            color: '#F8B752'
          };
      }
    },

    getExternalCodeIcon(externalCode) {
      switch (true) {
        case !!externalCode:
          return {
            icon: 'mdi-alpha-e-circle-outline',
            color: 'primary'
          };

        default:
          return {};
      }
    },
    resetPagination() {
      this.pagination = {
        page: 1,
        itemsPerPage: this.pagination.itemsPerPage
      };
    },
    getStatusByName(statusName) {
      const status = DEFAULTS.ROWS_STATUSES[statusName];
      if (status) return status;
      else {
        return DEFAULTS.ROWS_STATUSES.not_reviewed;
      }
    },
    getFilteredHeaders() {
      return this.filters.showPrice || this.filters.showPrice === undefined
        ? this.headers
        : this.headers.filter(
            (header) => !header.type || header.type !== 'pricePart'
          );
    },
    recurentTableSet(rows, numberPerStep, currentCall, opt) {
      if (opt) {
        this.internalRows = rows;
        this.internalLoading = false;
        return;
      }

      if (currentCall !== this.latestCall) return;

      if (!(rows && rows.length)) {
        this.internalLoading = false;
        return;
      }
      if (rows.length <= numberPerStep) {
        this.internalLoading = false;
        this.internalRows = [...this.internalRows, ...rows];

        return;
      } else {
        this.internalRows = [
          ...this.internalRows,
          ...rows.slice(0, numberPerStep)
        ];
        return setTimeout(() => {
          this.recurentTableSet(
            rows.slice(numberPerStep, rows.length),
            numberPerStep,
            currentCall
          );
        }, 50);
      }
    },
    getGroupByField() {
      switch (true) {
        case !!(this.params.multimatch && this.params.multimatch.length) ||
          !!this.filters.multimatch:
          this.groupBy = 'multimatch';
          return;
        case !!(this.params.groupBy && this.params.groupBy.length):
          this.groupBy = 'group_by';
          return;
        case !!(this.params.conflicts && this.params.conflicts.length):
          this.groupBy = 'conflicts';
          return;
        case !!this.params.lastSold:
          this.groupBy = 'sold result';
          return;

        default:
          this.groupBy = undefined;
          return;
      }
    },
    multiMatchSelected(row) {
      const currentMultimatchValue = this.multiMatchModels[row.multimatch];
      this.$emit('setStatus', {
        status: 'ignore',
        multimatch: row.multimatch,
        correct_rows_ids: currentMultimatchValue
      });
    },
    reCreateMultiMatch() {
      this.multiMatchModels = {};
      _.uniqBy(this.rows, (el) => el.multimatch).forEach((row) => {
        const withCorrectStatus = this.rows
          .filter(
            (fRow) =>
              fRow.multimatch === row.multimatch &&
              fRow.status.name === 'correct_by_multimatch'
          )
          .map((fRow) => fRow.id);
        this.$set(
          this.multiMatchModels,
          row.multimatch,
          withCorrectStatus && withCorrectStatus.length ? withCorrectStatus : []
        );
      });
    },
    showStatusChangeMenu(e, { item }) {
      if (item.status.name === 'imported') return;
      const isItemValid = this.isItemValid(item);
      const isItemIgnored = item.category === 'ignore';
      const disabledStatusesList =
        isItemValid && !isItemIgnored ? [] : ['ready_for_import'];

      this.selectedRow = item;
      e.preventDefault();
      this.$nextTick(() => {
        this.$emit('showStatusChangeMenu', {
          disabledStatusesList,
          x: e.clientX,
          y: e.clientY,
          selectedRows: this.getSelectedRows()
        });
      });
    },
    showComments(row) {
      this.selectedRow = row;
      this.$emit('showComments');
    },
    selectAll({ items }) {
      this.selected = items
        .filter((row) => !this.isRowDisabled(row))
        .map((row) => row.id);
    },
    editFiled(item = {}, type) {
      this.editableValue = _.get(item, type);
      this.editableItem = String(item.id);
      this.editableType = String(type);
    },
    saveField(row) {
      _.set(row, this.editableType, this.editableValue);
      let response = {};
      _.set(response, this.editableType, this.editableValue);

      this.handleRowUpdate(row, response);

      this.editableItem = undefined;
      this.editableType = undefined;
    },
    handleRowUpdate(rowId, updatePartition) {
      this.$emit('onUpdateRow', rowId, updatePartition);
    },
    isRowDisabled(row) {
      return row.import_status === 'SUCCEED' && row.status.name === 'imported';
    },
    preventRightClick(e) {
      e.preventDefault();
      this.$emit('hideMenu');
    },
    showContextMenu(e, { item }) {
      this.selectedRow = item;
      e.preventDefault();
      this.$nextTick(() => {
        this.$emit('showMenu', {
          x: e.clientX,
          y: e.clientY,
          selectedRows: this.getSelectedRows()
        });
      });
    },
    getSelectedRows() {
      return this.selected && this.selected.length ? this.selected : [];
    },
    getSelectedRow() {
      return this.selectedRow;
    },
    getPagination() {
      return this.pagination;
    },
    resetSelection() {
      this.selected = [];
    },
    handleRowReviewedChange(row) {
      const { id, reviewed } = row;

      this.handleRowUpdate(id, { reviewed: !reviewed });
    },
    checkIsCategoryOverridable(category) {
      return NonOverridableCategories.includes(category);
    },
    getMappedTypeOverrideOptions({ category }) {
      return this.checkIsCategoryOverridable(category)
        ? CategoriesOverridableOptions
        : CategoriesOptions;
    }
  }
};
</script>

<style lang="scss">
.file-edit-content {
  .multimatch-radio-group {
    display: flex;
    flex-direction: column;
    align-items: center;

    .v-input__slot {
      justify-content: center;
    }
  }

  .v-data-table__wrapper {
    height: 53vh;
  }

  .v-data-table {
    tr {
      position: relative;
    }

    .table-item-info {
      width: 0;
      visibility: hidden;
      padding: 0;
    }

    .item-warning {
      position: absolute;
      top: 0;
      left: 0;
    }

    .recommended-for-import {
      td {
        border-color: rgba(125, 172, 30, 0.5) !important;
      }
    }

    .item-invalid {
      td {
        border-color: rgba(255, 165, 0, 0.7) !important;
      }
    }

    td {
      height: 6.5vh !important;

      textarea {
        max-height: 6vh !important;
      }

      padding: 1px !important;
      margin: 0px !important;
      border: thin solid rgba(0, 0, 0, 0.12) !important;

      .v-text-field.v-text-field--enclosed:not(.v-text-field--rounded)
        > .v-input__control
        > .v-input__slot,
      .v-text-field.v-text-field--enclosed .v-text-field__details {
        padding: 1px !important;
        margin: 0px !important;
      }

      .v-text-field.v-text-field--enclosed {
        padding: 0px !important;
        margin: 0px !important;
      }
    }
  }

  tr:nth-child(even) {
    background: #f5f9fb;
  }

  tr:nth-child(odd) {
    background: #ffffff;
  }

  tr:not(.v-row-group__header) {
    .editable {
      border: thin solid #3a76d1 !important;

      .editable-textarea {
        font-size: 14px !important;
        line-height: 24px !important;
      }
    }

    .text-start {
      text-align: center !important;
    }
  }

  .selected-row {
    background-color: white !important;
  }
}
</style>
